import { useHostContext } from '@waforms/app-sdk/client';
import { Button, EmptyState, Spinner } from '@waforms/ui';
import { MonitorOff, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { Callout } from './components/common.tsx';
import { useHostStatus } from './lib/api.ts';
import { config } from './lib/config.ts';
import { ApiKeysPage } from './pages/ApiKeysPage.tsx';
import { ConversationTab } from './pages/ConversationTab.tsx';
import { FormsList } from './pages/FormsList.tsx';

const loadFormPage = async () => ({ Component: (await import('./pages/FormPage.tsx')).FormPage });

const router = createBrowserRouter(
  [
    { path: '/', element: <FormsList /> },
    { path: '/api-keys', element: <ApiKeysPage /> },
    // The builder, submissions and stats are loaded on demand (the conversation tab stays small).
    { path: '/forms/:formId', lazy: loadFormPage },
    { path: '/forms/:formId/:tab', lazy: loadFormPage },
    // Framed by the dashboard at <URL><conversation_tab_path> (config/w3send_apps.yml).
    { path: '/conversation', element: <ConversationTab /> },
    {
      path: '*',
      element: <EmptyState title="Page not found" description="This page does not exist in the app." />,
    },
  ],
  { basename: config.basePath || '/' },
);

const HOST_NAME = config.hostKind === 'chatwoot' ? 'the CRM' : 'w3send';

/** Chatwoot host: says so when no administrator has connected the CRM for background work yet. */
function CrmConnectionBanner() {
  const status = useHostStatus(config.hostKind === 'chatwoot');
  if (!status.data || status.data.crm_connected) return null;
  return (
    <Callout tone="warning" className="m-4 mb-0" title="The CRM is not connected yet">
      <p>{status.data.message}</p>
    </Callout>
  );
}

/** Renders the app only once the dashboard handed over a session. */
function SessionGate({ children }: { children: ReactNode }) {
  const host = useHostContext();

  if (host.status === 'loading') {
    return (
      <div className="flex h-screen items-center justify-center text-n-slate-11">
        <Spinner label={`Connecting to ${HOST_NAME}`} />
      </div>
    );
  }
  if (host.status === 'error') {
    return (
      <EmptyState
        className="h-screen"
        icon={<TriangleAlert />}
        title="Could not start the app"
        description={host.error ?? 'Unknown error'}
        action={
          <Button size="sm" variant="faded" onClick={() => window.location.reload()}>
            Reload
          </Button>
        }
      />
    );
  }
  if (host.status === 'standalone' && !host.hasToken) {
    return (
      <EmptyState
        className="h-screen"
        icon={<MonitorOff />}
        title={`Open this app from ${HOST_NAME}`}
        description={`It runs inside the ${HOST_NAME} dashboard, which signs you in.`}
      />
    );
  }
  return (
    <>
      {config.hostKind === 'chatwoot' && <CrmConnectionBanner />}
      {children}
    </>
  );
}

export function App() {
  return (
    <SessionGate>
      <RouterProvider router={router} />
    </SessionGate>
  );
}
